/**
 * Reading the applied priority / rush delivery option off `servicabilityInfo`.
 *
 * The payload arrives in two shapes:
 *
 *  - nested (legacy): `priorityDelivery` / `rushDelivery` sub-objects carrying
 *    `isApplied`, `deliveryDate`, `fee.value` and `deliverySLAPD` / `deliverySLARush`.
 *  - flat (multiseller): `shippingAction` names the applied option and
 *    `deliverySLA` / `deliveryDate` sit at the top level. No fee is sent there,
 *    so it reads as 0 — nothing else on the payload carries one.
 *
 * Shared by the shipping page (Shipping.js, which forwards the values on to the
 * payment page) and the EDD item list (edd/index.js, which renders the PD / RUSH
 * badges), so both agree on when an option counts as applied.
 */
export const SHIPPING_ACTION = Object.freeze({ PRIORITY: 'PRIORITY', RUSH: 'RUSH' });

const NOT_APPLIED = Object.freeze({
  isApplied: false,
  deliveryDate: '',
  fee: 0,
  deliverySLA: ''
});

/**
 * @param {object} servicabilityInfo cart item's servicabilityInfo (either shape)
 * @param {string} method SHIPPING_ACTION.PRIORITY | SHIPPING_ACTION.RUSH
 * @returns {{isApplied: boolean, deliveryDate: string, fee: number, deliverySLA: string}}
 */
export const readAppliedDeliveryOption = (servicabilityInfo, method) => {
  const info = servicabilityInfo || {};
  const isPriority = method === SHIPPING_ACTION.PRIORITY;

  // Either sub-object marks this as the legacy shape, and then only `isApplied`
  // decides — the flat read is skipped entirely. That matters because
  // `shippingAction` is not new: it ships on legacy responses too, so falling
  // back on it whenever `isApplied` was false would count an option the user
  // never applied.
  if (info.priorityDelivery || info.rushDelivery) {
    const nested = isPriority ? info.priorityDelivery : info.rushDelivery;
    if (!nested || !nested.isApplied) return { ...NOT_APPLIED };
    return {
      isApplied: true,
      deliveryDate: nested.deliveryDate || '',
      fee: (nested.fee && nested.fee.value) || 0,
      deliverySLA: (isPriority ? nested.deliverySLAPD : nested.deliverySLARush) || ''
    };
  }

  const applied =
    String(info.shippingAction || '')
      .trim()
      .toUpperCase() === method;
  if (!applied) return { ...NOT_APPLIED };

  return {
    isApplied: true,
    deliveryDate: info.deliveryDate || '',
    fee: 0,
    deliverySLA: info.deliverySLA || ''
  };
};
